import * as React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { siteConfig } from "../config/siteConfig";
import ThemeToggle from "./ThemeToggle";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/projects", label: "Work" },
  { to: "/about", label: "About" },
  { to: "/security", label: "Security" },
  { to: "/contact", label: "Contact" },
];

const linkClass = ({ isActive }: { isActive: boolean }) =>
  [
    "relative px-3 py-2 text-sm transition-colors",
    isActive ? "text-ink" : "text-muted hover:text-ink",
  ].join(" ");

export default function Layout() {
  const [open, setOpen] = React.useState(false);
  const { pathname } = useLocation();

  // Close the mobile sheet whenever the route changes.
  React.useEffect(() => setOpen(false), [pathname]);

  // Lock scroll behind the mobile sheet.
  React.useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-ink">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-accent focus:px-4 focus:py-2 focus:text-sm focus:text-accent-ink"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-40 border-b border-line/70 bg-canvas/80 backdrop-blur-xl">
        <nav className="container-max flex h-16 items-center justify-between gap-4">
          <Link
            to="/"
            className="group flex items-center gap-2.5"
            aria-label={`${siteConfig.name} — home`}
          >
            <span className="grid h-8 w-8 place-items-center rounded-lg border border-accent/40 bg-accent/10 font-mono text-xs font-semibold text-accent">
              {siteConfig.initials}
            </span>
            <span className="font-mono text-sm tracking-tight text-ink">
              raviteja
              <span className="text-accent">.nb</span>
            </span>
          </Link>

          <div className="hidden items-center gap-1 md:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={linkClass}
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-active"
                        className="absolute inset-x-2 -bottom-px h-px bg-accent"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <a
              href={siteConfig.resumeUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-outline hidden !py-2 text-xs sm:inline-flex"
            >
              Résumé
            </a>
            <button
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line text-muted transition hover:text-ink md:hidden"
              aria-label="Toggle menu"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                {open ? (
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                ) : (
                  <path
                    d="M4 7h16M4 12h16M4 17h16"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                )}
              </svg>
            </button>
          </div>
        </nav>

        <AnimatePresence>
          {open && (
            <motion.div
              key="mobile-nav"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border-t border-line bg-canvas md:hidden"
            >
              <div className="container-max flex flex-col py-3">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      [
                        "rounded-lg px-3 py-3 text-base transition",
                        isActive
                          ? "bg-raised text-ink"
                          : "text-muted hover:bg-raised hover:text-ink",
                      ].join(" ")
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <a
                  href={siteConfig.resumeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 rounded-lg px-3 py-3 text-base text-muted transition hover:bg-raised hover:text-ink"
                >
                  Résumé ↗
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}

function Footer() {
  const { links, name, email_g } = siteConfig;
  return (
    <footer className="mt-20 border-t border-line">
      <div className="container-max flex flex-col gap-6 py-10 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="font-mono text-sm text-ink">
            raviteja<span className="text-accent">.nb</span>
          </div>
          <p className="mt-1.5 text-sm text-faint">
            {siteConfig.role} · India (IST)
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <FooterLink href={links.github}>GitHub</FooterLink>
          <FooterLink href={links.linkedin}>LinkedIn</FooterLink>
          <FooterLink href={links.leetcode}>LeetCode</FooterLink>
          <FooterLink href={`mailto:${email_g}`}>Email</FooterLink>
        </div>
      </div>
      <div className="container-max pb-8 text-2xs text-faint">
        © {new Date().getFullYear()} {name}
      </div>
    </footer>
  );
}

function FooterLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-muted transition hover:text-accent"
    >
      {children}
    </a>
  );
}
